import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Card, CardActionArea, CardContent, LinearProgress, Paper, Stack, TextField, Typography, InputAdornment, Backdrop, CircularProgress, Snackbar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Divider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Header } from 'components/Header';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ServiceRequestSummary } from '../services/types';
import { CUSTOMER_NAME_PLACEHOLDER } from '../services/const';
import { bulkUpdateTracking, getServiceRequestsByStatusAndStoreId } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { formatOrderDateTime } from 'utils/orderMeta';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Scanner as QrScannerComp } from '@yudiel/react-qr-scanner';
import { ShipmentGroup, UPS_TRACKING_PREFIX_REGEX } from 'services/util';

interface BulkTrackingUpdatePageProps {
  externalIdentity: string;
  storeId: string;
  associateId: string;
  associateEmail: string;
  associateName?: string;
}

type TrackingGroup = {
  id: string;
  trackingReference: string;
  serviceItemIds: string[];
};

const createTrackingGroup = (): TrackingGroup => ({
  id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  trackingReference: '',
  serviceItemIds: [],
});

const getCustomerName = (value?: string | null) =>
  (value ?? '').toString().trim() || CUSTOMER_NAME_PLACEHOLDER;

const BulkTrackingUpdatePage: React.FC<BulkTrackingUpdatePageProps> = ({ externalIdentity, storeId, associateId, associateEmail, associateName }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState<ServiceRequestSummary[]>([]);
  const [groups, setGroups] = useState<TrackingGroup[]>(() => [createTrackingGroup()]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const PAGE_SIZE = 9999;
  // lightweight feedback with navigation; avoid snackbar to keep page lean
  const [snack, setSnack] = useState<string | null>(null);
  // scanner dialog state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerGroupId, setScannerGroupId] = useState<string | null>(null);
  const [scannerField, setScannerField] = useState<'tracking' | null>(null);
  const [scannerKey, setScannerKey] = useState(0);
  const [scanning, setScanning] = useState(true);
  const [cameraHint, setCameraHint] = useState<string | null>(null);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [selectionGroupId, setSelectionGroupId] = useState<string | null>(null);
  const [selectionSearch, setSelectionSearch] = useState('');
  const [selectionWorkingIds, setSelectionWorkingIds] = useState<string[]>([]);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const isSecureContext =
    typeof window !== 'undefined' &&
    (window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname));
  const scannerContainerRef = useRef<HTMLDivElement | null>(null);
  const resolvedAssociateName = useMemo(
    () => (associateName?.trim() || associateEmail || associateId),
    [associateId, associateEmail, associateName]
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const onScannerError = useCallback((err: any) => {
    console.error(err);
    const name = err?.name || '';
    const message = err?.message || '';
    if (
      /NotAllowedError|AbortError|NotReadableError|OverconstrainedError/i.test(name) ||
      /camera|permission|aborted|ended/i.test(message)
    ) {
      // Keep dialog open and replace scanner with Scan Again
      setCameraHint('Camera closed. Tap Scan Again to reopen.');
      setScanning(false);
      setScannerOpen(false)
    }
  }, []);

  useEffect(() => {
    if (!scannerOpen || !scanning) return;
    const node = scannerContainerRef.current;
    if (!node) return;
    const video: HTMLVideoElement | null = node.querySelector('video');
    if (!video) return;
    const handleEnded = () => {
      setCameraHint('Camera closed. Tap Scan Again to reopen.');
      setScanning(false);
      setScannerOpen(false)
    };
    video.addEventListener('pause', handleEnded);
    video.addEventListener('ended', handleEnded);
    (video as any).addEventListener?.('webkitendfullscreen', handleEnded);
    return () => {
      video.removeEventListener('pause', handleEnded);
      video.removeEventListener('ended', handleEnded);
      (video as any).removeEventListener?.('webkitendfullscreen', handleEnded);
    };
  }, [scannerOpen, scanning]);
  const ordersById = useMemo(() => {
    const map: Record<string, ServiceRequestSummary> = {};
    orders.forEach((order) => {
      const id = ((order as any)?.serviceItemId || '').toString();
      if (id) map[id] = order;
    });
    return map;
  }, [orders]);

  // Fetch a page of results with status=OPEN (1) and location=karville
  const fetchPage = useCallback(
    async (pageToFetch: number) => {
      const isFirst = pageToFetch === 0;
      if (isFirst) {
        setLoading(true);
        setOrders([]); // clear while loading fresh
      } else {
        setPageLoading(true);
      }
      try {
        const limit = PAGE_SIZE;
        const offset = pageToFetch * limit;
        const data = await getServiceRequestsByStatusAndStoreId(
          externalIdentity,
          1, // OPEN
          storeId,
          limit,
          offset,
          'Kerrville_all'
        );
        if (isFirst) {
          setOrders(data || []);
        } else {
          setOrders(prev => [...prev, ...(data || [])]);
        }
        setHasMore((data?.length || 0) === limit);
        setPage(pageToFetch);
      } catch {
        // noop
        setHasMore(false);
      } finally {
        if (isFirst) setLoading(false);
        else setPageLoading(false);
      }
    },
    [externalIdentity, storeId]
  );

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  useEffect(() => {
    const availableIds = new Set(Object.keys(ordersById));
    setGroups((prev) => {
      let changed = false;
      const next = prev.map((group) => {
        const filteredIds = group.serviceItemIds.filter((id) => availableIds.has(id));
        if (filteredIds.length !== group.serviceItemIds.length) {
          changed = true;
          return { ...group, serviceItemIds: filteredIds };
        }
        return group;
      });
      return changed ? next : prev;
    });
  }, [ordersById]);

  const addGroup = useCallback(() => {
    setGroups((prev) => [...prev, createTrackingGroup()]);
  }, []);

  const closeSelectionDialog = useCallback(() => {
    setSelectionDialogOpen(false);
    setSelectionGroupId(null);
    setSelectionSearch('');
    setSelectionWorkingIds([]);
  }, []);

  const openSelectionDialog = useCallback((groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    setSelectionGroupId(groupId);
    setSelectionSearch('');
    setSelectionWorkingIds(group ? [...group.serviceItemIds] : []);
    setSelectionDialogOpen(true);
  }, [groups]);

  const removeGroup = useCallback((groupId: string) => {
    setGroups((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((group) => group.id !== groupId);
    });
    if (selectionGroupId === groupId) {
      closeSelectionDialog();
    }
  }, [closeSelectionDialog, selectionGroupId]);

  const removeItemFromGroup = useCallback((groupId: string, serviceItemId: string) => {
    setGroups((prev) => prev.map((group) => {
      if (group.id !== groupId) return group;
      return { ...group, serviceItemIds: group.serviceItemIds.filter((id) => id !== serviceItemId) };
    }));
    if (selectionDialogOpen && selectionGroupId === groupId) {
      setSelectionWorkingIds((prev) => prev.filter((id) => id !== serviceItemId));
    }
  }, [selectionDialogOpen, selectionGroupId]);

  const toggleSelectionWorkingId = useCallback((serviceItemId: string) => {
    setSelectionWorkingIds((prev) => {
      if (prev.includes(serviceItemId)) {
        return prev.filter((id) => id !== serviceItemId);
      }
      return [...prev, serviceItemId];
    });
  }, []);

  const applySelection = useCallback(() => {
    if (!selectionGroupId) {
      closeSelectionDialog();
      return;
    }
    setGroups((prev) => prev.map((group) => {
      if (group.id !== selectionGroupId) return group;
      return { ...group, serviceItemIds: [...selectionWorkingIds] };
    }));
    closeSelectionDialog();
  }, [closeSelectionDialog, selectionGroupId, selectionWorkingIds]);

  const selectionAvailableOrders = useMemo(() => {
    if (!selectionGroupId) return [];
    const taken = new Set<string>();
    groups.forEach((group) => {
      if (group.id === selectionGroupId) return;
      group.serviceItemIds.forEach((id) => taken.add(id));
    });
    const query = selectionSearch.trim().toLowerCase();
    const selectedSet = new Set(selectionWorkingIds);
    const list = orders.filter((order) => {
      const id = ((order as any)?.serviceItemId || '').toString();
      if (!id) return false;
      if (taken.has(id) && !selectedSet.has(id)) return false;
      // Exclude orders that have the "Kerrville" + "status 10" service
      const hasExcludedService = (
        Array.isArray(order.services) ? order.services : []
      ).some(
        (service) =>
          service.location_type === 'Kerrville' && (String(service.status) === "10") 
      );
      // If the order has this service, we return false to exclude it from the list
      if (hasExcludedService) return false;
      
      if (!query) return true;
      const fields = [
        order.orderExternalId,
        order.sku,
        order.tag,
        getCustomerName(order.customerName),
        order.customerEmail,
      ];
      return fields.some((value) => (value || '').toString().toLowerCase().includes(query));
    });
    return list.sort((a, b) => {
      const aId = ((a as any)?.serviceItemId || '').toString();
      const bId = ((b as any)?.serviceItemId || '').toString();
      const aSelected = selectedSet.has(aId);
      const bSelected = selectedSet.has(bId);
      if (aSelected === bSelected) {
        const aLabel = (a.orderExternalId || '').toString();
        const bLabel = (b.orderExternalId || '').toString();
        return aLabel.localeCompare(bLabel);
      }
      return aSelected ? -1 : 1;
    });
  }, [groups, orders, selectionGroupId, selectionSearch, selectionWorkingIds]);

  useEffect(() => {
    if (!selectionDialogOpen) return;
    setSelectionWorkingIds((prev) => {
      const filtered = prev.filter((id) => !!ordersById[id]);
      if (filtered.length === prev.length) return prev;
      return filtered;
    });
  }, [ordersById, selectionDialogOpen]);

  // infinite scroll
  useEffect(() => {
    if (!hasMore || loading || pageLoading) return;
    const node = sentinelRef.current;
    if (!node) return;
    const obs = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) fetchPage(page + 1);
    }, { rootMargin: '600px 0px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, loading, pageLoading, fetchPage, page]);

  const canSubmit = useMemo(() => {
    const anySelected = groups.some((group) => group.serviceItemIds.length > 0);
    if (!anySelected) return false;
    const hasIncompleteGroup = groups.some((group) => {
      if (!group.serviceItemIds.length) return false;
      return !group.trackingReference.trim();
    });
    return !hasIncompleteGroup;
  }, [groups]);

  const itemsLoaded = !loading;
  const canSelectItems = itemsLoaded && orders.length > 0;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const payloadItems = groups.flatMap((group) => {
        const trackingReference = group.trackingReference.trim();
        if (!trackingReference) return [];
        return group.serviceItemIds.map((serviceItemId) => ({
          service_item_id: serviceItemId,
          tracking_reference: trackingReference,
        }));
      });

      if (!payloadItems.length) {
        setSnack('Add at least one tracking group with order items.');
        return;
      }

      await bulkUpdateTracking(externalIdentity, {
        data: payloadItems,
        associate_id: associateId,
        associate_name: resolvedAssociateName,
      });
      // Reset local state and refresh list
      setGroups([createTrackingGroup()]);
      await fetchPage(0);
      setSnack('Tracking updated successfully');
    } catch (err) {
      logApiError('Bulk tracking update submission', err);
      setSnack(formatApiErrorMessage(err, 'Failed to submit updates'));
    } finally {
      setSubmitting(false);
    }
  };
  const hasUnsubmittedOrders = groups.some(
    (group) => group.serviceItemIds?.length > 0
  );
  const handleBackClick = () => {
    if (hasUnsubmittedOrders) {
      setLeaveDialogOpen(true);
      return;
    }
    navigate(-1);
  };
  const getTrackingReferenceError = (group: ShipmentGroup): string => {

  const trackingReference = (group.trackingReference ?? '').trim();

  if (group.serviceItemIds.length > 0 && !trackingReference) {
    return 'Required when items are selected.';
  }

  if (trackingReference  && !UPS_TRACKING_PREFIX_REGEX.test(trackingReference)) {
    return 'Tracking number must start with 1Z';
  }
  return '';
  };

  const hasTrackingErrors = groups.some(
  (group) => Boolean(getTrackingReferenceError(group))
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header title="Ship to Product Care" showBackButton onBack={() => handleBackClick()} sticky elevateOnScroll align="left" />

      {loading && (
        <Box sx={{ px: 2, pt: 1 }}>
          <LinearProgress />
        </Box>
      )}

      <Paper
        elevation={0}
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          p: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background: alpha(theme.palette.primary.light, 0.06),
        })}
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Add one or more shipments. Each shipment can include multiple order items, but an item can only belong to a single shipment.
          </Typography>

          <Stack spacing={1.5}>
            {orders.length === 0 && !loading && (
              <Typography variant="body2" color="text.secondary">No open orders found.</Typography>
            )}
            {groups.map((group, index) => {
              const trackingError = getTrackingReferenceError(group);
              const selectedOrders = group.serviceItemIds
                .map((id) => ordersById[id])
                .filter(Boolean) as ServiceRequestSummary[];

              return (
                <Card
                  key={group.id}
                  variant="outlined"
                  sx={(theme) => ({
                    borderRadius: 3,
                    borderColor: alpha(theme.palette.divider, 0.85),
                  })}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight={700}>Shipment {index + 1}</Typography>
                        {groups.length > 1 && (
                          <IconButton
                            size="small"
                            aria-label="Remove shipment"
                            onClick={() => removeGroup(group.id)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>

                      <TextField
                        label="Tracking Reference"
                        placeholder={isMobile ? "Starts with 1Z" : "Tracking number must start with 1Z"}
                        value={group.trackingReference}
                        onChange={(e) => {
                          const value = e.target.value;
                          setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, trackingReference: value } : g)));
                        }}
                        error={Boolean(trackingError)}
                        helperText={trackingError || undefined}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocalShippingOutlinedIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                aria-label="Scan tracking reference"
                                onClick={() => {
                                  setScannerGroupId(group.id);
                                  setScannerField('tracking');
                                  setScannerOpen(true);
                                  setScanning(true);
                                  setCameraHint(null);
                                  setScannerKey((k) => k + 1);
                                }}
                              >
                                <QrCode2OutlinedIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={(theme) => ({
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.background.paper, 0.6),
                            transition: 'box-shadow .2s ease',
                            '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                            '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                          },
                        })}
                      />
                      <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="subtitle2" fontWeight={700}>
                            Shipment Items
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openSelectionDialog(group.id)}
                            disabled={!canSelectItems}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                          >
                            {selectedOrders.length ? 'Manage Orders' : 'Select Eligible Orders'}
                          </Button>
                        </Stack>
                        {selectedOrders.length ? (
                          <Stack spacing={1}>
                            {selectedOrders.map((order) => {
                              const serviceItemId = ((order as any)?.serviceItemId || '').toString();
                              const customerName = getCustomerName(order.customerName);
                              const subtitle = [order.sku, customerName].filter(Boolean).join(' · ');
                              return (
                                <Paper
                                  key={`${group.id}-${serviceItemId}`}
                                  variant="outlined"
                                  sx={(theme) => ({
                                    p: 1.1,
                                    borderRadius: 2,
                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                  })}
                                >
                                  <Stack spacing={0.25}>
                                    <Typography variant="body2" fontWeight={600}>
                                      Order {order.orderExternalId || '—'}
                                    </Typography>
                                    {subtitle && (
                                      <Typography variant="caption" color="text.secondary">
                                        {subtitle}
                                      </Typography>
                                    )}
                                  </Stack>
                                  <IconButton
                                    size="small"
                                    aria-label="Remove item"
                                    onClick={() => removeItemFromGroup(group.id, serviceItemId)}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Paper>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No items selected yet.
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Items already assigned to another shipment are unavailable when adding more.
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          <Button
            type="button"
            variant="outlined"
            onClick={addGroup}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Add Another Shipment
          </Button>

          {hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
        </Stack>

      </Paper>

      <Box mt={3} mx={2} mb={4}>
        <Button
          variant="contained"
          fullWidth
          disabled={!itemsLoaded || !canSubmit || submitting || hasTrackingErrors}
          onClick={handleSubmit}
          sx={(theme) => ({
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            '&.Mui-disabled': {
              color: theme.palette.common.white,
              backgroundColor: alpha(theme.palette.primary.main, 0.35),
            },
          })}
        >
          {submitting ? 'Submitting…' : 'Submit Updates'}
        </Button>
      </Box>
      <Backdrop open={submitting} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />

      {/* Item selection dialog */}
      <Dialog
        open={selectionDialogOpen}
        onClose={closeSelectionDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Order Items</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              value={selectionSearch}
              onChange={(e) => setSelectionSearch(e.target.value)}
              placeholder="Search orders (ID, SKU, customer, tag, email)"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={(theme) => ({
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  transition: 'box-shadow .2s ease',
                  '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                  '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                },
              })}
            />
            <Typography variant="body2" color="text.secondary">
              {selectionWorkingIds.length ? `${selectionWorkingIds.length} item${selectionWorkingIds.length === 1 ? '' : 's'} selected` : 'No items selected yet.'}
            </Typography>
            {selectionAvailableOrders.length > 0 && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const ids = selectionAvailableOrders.map((order) => ((order as any)?.serviceItemId || '').toString()).filter(Boolean);
                    setSelectionWorkingIds(ids);
                  }}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Select All
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setSelectionWorkingIds([])}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Clear
                </Button>
              </Stack>
            )}
            <Stack spacing={1.25}>
              {selectionAvailableOrders.length ? (
                selectionAvailableOrders.map((order) => {
                  const id = ((order as any)?.serviceItemId || '').toString();
                  const isSelected = selectionWorkingIds.includes(id);
                  const chips: Array<{ label: string; key: string }> = [];
                  if (order.sku) chips.push({ label: `SKU ${order.sku}`, key: 'sku' });
                  if (order.tag) chips.push({ label: `Tag ${order.tag}`, key: 'tag' });
                  const secondary = [getCustomerName(order.customerName), order.customerEmail]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <Card
                      key={id}
                      variant="outlined"
                      sx={(theme) => ({
                        borderRadius: 3,
                        borderColor: isSelected ? alpha(theme.palette.success.main, 0.6) : alpha(theme.palette.divider, 0.9),
                        bgcolor: isSelected ? alpha(theme.palette.success.main, 0.1) : theme.palette.background.paper,
                        boxShadow: isSelected ? '0 8px 24px rgba(0,0,0,0.14)' : '0 1px 2px rgba(0,0,0,0.06)',
                        transition: 'all .18s ease',
                      })}
                    >
                      <CardActionArea onClick={() => toggleSelectionWorkingId(id)} sx={{ borderRadius: 3 }}>
                        <CardContent>
                          <Stack spacing={1.1}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Typography variant="subtitle2" fontWeight={700}>
                                Order {order.orderExternalId || '—'}
                              </Typography>
                              {isSelected && <CheckCircleOutlineIcon color="success" fontSize="small" />}
                            </Stack>
                            {chips.length > 0 && (
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {chips.map((chip) => (
                                  <Chip
                                    key={`${id}-${chip.key}`}
                                    label={chip.label}
                                    size="small"
                                    sx={(theme) => ({
                                      borderRadius: 2,
                                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                      color: theme.palette.primary.main,
                                    })}
                                  />
                                ))}
                              </Stack>
                            )}
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {secondary || '—'}
                              </Typography>
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                  display: { xs: 'none', sm: 'block' },
                                  height: '12px',
                                  alignSelf: 'center',
                                  mx: 1,
                                  borderRightWidth: 2,
                                  borderColor: 'grey.500'
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ marginLeft: { xs: '0px !important', sm: '8px !important'} }}
                              >
                                Created Date :
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ marginLeft: { xs: '0px !important', sm: '8px !important'} }}

                              >
                                {`${formatOrderDateTime(order?.created)}`}
                              </Typography>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No orders available. Adjust your search or release items from other shipments.
                </Typography>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeSelectionDialog} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={applySelection}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Apply Selection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scanner Dialog */}
      <Dialog
        open={scannerOpen}
        onClose={() => {
          setScanning(false);
          setScannerOpen(false);
          setScannerGroupId(null);
          setScannerField(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Scan Tracking Number</DialogTitle>
        <DialogContent>
          <Box ref={scannerContainerRef} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {scannerOpen && isSecureContext ? (
              scanning ? (
                <QrScannerComp
                  key={scannerKey}
                  onScan={(codes: any[]) => {
                    const text = Array.isArray(codes) && codes[0]?.rawValue ? codes[0].rawValue : (codes?.[0]?.rawValue || codes?.[0]?.rawValueText || '');
                    if (!text) return;
                    const groupId = scannerGroupId;
                    if (groupId && scannerField) {
                      setGroups((prev) =>
                        prev.map((group) =>
                          group.id === groupId ? { ...group, trackingReference: text } : group
                        )
                      );
                    }
                    setScannerOpen(false);
                    setScanning(false);
                    setScannerGroupId(null);
                    setScannerField(null);
                  }}
                  onError={onScannerError}
                  constraints={{ facingMode: 'environment' }}
                  styles={{ video: { width: '100%' } }}
                  formats={['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8', 'pdf417']}
                />
              ) : (
                <Stack spacing={1} alignItems="stretch">
                  <Button
                    variant="outlined"
                    onClick={() => { setScanning(true); setScannerKey((k) => k + 1); setCameraHint(null); }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    Scan Again
                  </Button>
                  {cameraHint && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {cameraHint}
                    </Typography>
                  )}
                </Stack>
              )
            ) : (
              <Typography variant="body2" color="text.secondary">
                Camera access requires HTTPS or localhost. Please open the app over HTTPS (or localhost) and try again.
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Leaving without submitting dialog */}
      <Dialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Proceed without submitting?</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You have unsubmitted order(s). Would you like to proceed?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Select Cancel to go back and process the shipment.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLeaveDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setLeaveDialogOpen(false);
              navigate(-1);
            }}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

    </Box>

  );
};

export default BulkTrackingUpdatePage;
